import { useState } from 'react'
import { Chip, Stack, Tooltip, Typography, Autocomplete, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import type { MultiTimeframeEntry } from '~/state/useMultiTimeframeState'
import { CONTROLS, CARDS } from '~/components/Charts/chrome/chartChrome'

// NOTE: Rendered by ReviewPageScaffold on multi-timeframe pages (multiTimeframe state non-null).
// Prefer the canonical `AbsoluteTimeframe` type from `./TimeframeSelect` over this
// structurally-identical `CampaignOption` if the type is ever unified.
export interface CampaignOption {
  value: string
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 6: present for campaign-sourced options — carried onto the added
   *  entry as MultiTimeframeEntry.campaign (id presence = campaign entry). */
  campaign?: string
  /** Whether the campaign carries a non-empty attribution predicate (Task 7 resolves it
   *  per-arm). Informational only here — the chip badge keys off the entry's campaign id. */
  hasPredicate?: boolean
}

interface Props {
  entries: MultiTimeframeEntry[]
  options: CampaignOption[]
  onAdd: (e: MultiTimeframeEntry) => void
  onRemove: (i: number) => void
}

/** Wave-5 §1 (D2, band Option A): ONE horizontal row — the old vertical Box (picker
 *  stacked over hint/entry rows) made the row-2 zone 89px tall vs the granularity
 *  zone's ~53px. Entry dates move into the chip tooltip: inline they'd re-widen the
 *  row into wrapping and regrow the zone (JL-accepted trade-off, spec §1). Wrap stays
 *  allowed for many entries — content-honest, same philosophy as whole-zone wrap. */
export function MultiTimeframePicker({ entries, options, onAdd, onRemove }: Props) {
  // Wave-6 §5a: controlled input — see the onInputChange allow-list below; onChange
  // clears on add so a selection never leaves residual filter text (wave-5 gate bug).
  const [inputValue, setInputValue] = useState('')

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      data-testid="multi-tf-row"
    >
      <Autocomplete
        size="small"
        sx={{ minWidth: 280 }}
        options={options}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          // Only user-intentional edits write the input state: 'input' (typing) and
          // 'clear'. Everything MUI emits on its own — the post-select echo and blur/
          // value-prop syncs — arrives as 'reset' in MUI 5 ('input'|'reset'|'clear' is
          // the full v5 reason vocabulary); letting it through would repopulate the
          // input with the picked label and silently FILTER the next dropdown open
          // (the wave-5 gate bug). onChange below clears on add regardless of ordering.
          if (reason === 'input' || reason === 'clear') setInputValue(v)
        }}
        onChange={(_, opt) => {
          if (opt) {
            onAdd({
              label: opt.label,
              from: opt.from,
              to: opt.to,
              ...(opt.campaign ? { campaign: opt.campaign } : {}),
            })
            setInputValue('')
          }
        }}
        renderInput={(params) => <TextField {...params} label={CONTROLS.addTimeframe} />}
      />
      {entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {CARDS.useMainTimeframeHint}
        </Typography>
      ) : (
        entries.map((e, i) => (
          <Tooltip key={`${e.label}-${i}`} title={`${e.from} → ${e.to}`}>
            <Chip
              label={
                e.campaign ? (
                  <>
                    {e.label}
                    <FilterAltOutlinedIcon
                      data-testid="multi-tf-campaign-badge"
                      fontSize="inherit"
                      aria-label={CONTROLS.campaignHasFilters}
                      sx={{ ml: 0.5, verticalAlign: 'middle' }}
                    />
                  </>
                ) : (
                  e.label
                )
              }
              onDelete={() => onRemove(i)}
              deleteIcon={
                <CloseIcon aria-label={`${CONTROLS.removeAria} ${e.label}`} fontSize="small" />
              }
            />
          </Tooltip>
        ))
      )}
    </Stack>
  )
}
